import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios';

// API
import { api } from '../configs/api/api';

// Thunks
import { userLogoutNotValidToken } from '../store/user/user.thunks';
import { openNotification } from '../store/app-notifications/app-notifications.thunks';

/**
 * Custom React Hook for adding an Axios interceptor.
 */
export const useInterceptor = () => {
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();

  const requestSuccessHandler = (request: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
    // do something with request

    const token = localStorage.getItem('token');
    let authData = {};
    if (token) {
      authData = {
        Authorization: `Bearer ${token}`
      }
    }

    return {
      ...request,
      headers: request.headers.concat(authData)
    };
  }

  const responseSuccessHandler = (response: AxiosResponse): AxiosResponse => {
    // do something with response
    return {
      ...response
    };
  }

  const requestErrorHandler = (error: AxiosError): Promise<AxiosError> => {
    // dispatch(openNotification({
    //   type: 'error',
    //   description: error.message,
    // }));
    return Promise.reject(error);
  }

  const responseErrorHandler = (error: AxiosError): Promise<AxiosError> => {
    if (error.response?.status === 401) {
      if (!localStorage.getItem('tokenError')) {
        dispatch(userLogoutNotValidToken()).then(() => navigate('/sign-in'));
        setTimeout(() => {
          dispatch(openNotification({
            type: 'error',
            description: 'Your account currently not available. Please try log in again',
          }));
        }, 500);
      }
      localStorage.setItem('tokenError', '1');
      return Promise.reject();
    }
    localStorage.removeItem('tokenError');
    return Promise.reject(error);
  }

  const setResponseInterceptor = (): number => {
    /**
     * Axios interceptor for handling response.
     * @param {Object} response - HTTP response object.
     * @returns {Object} - HTTP response object.
     */
    return api.interceptors.response.use(
      (response) => responseSuccessHandler(response),
      /**
       * Axios interceptor for handling errors.
       * @param {Object} error - HTTP error object.
       * @returns {Promise} - Promise object that rejects with the error object.
       */
      (error) => responseErrorHandler(error)
    )
  }

  const setRequestInterceptor = (): number => {
    /**
     * Axios interceptor for handling request.
     * @param {Object} request - HTTP request object.
     * @returns {Object} - HTTP request object.
     */
    return api.interceptors.request.use(
      (request) => requestSuccessHandler(request),
      /**
       * Axios interceptor for handling errors.
       * @param {Object} error - HTTP error object.
       * @returns {Promise} - Promise object that rejects with the error object.
       */
      (error) => requestErrorHandler(error)
    )
  }

  useEffect(() => {
    /**
     Sets up the axios response interceptor.
     @function
     @returns {number} The ID of the axios response interceptor
     */
    const interceptorResponse = setResponseInterceptor();

    /**
     Sets up the axios request interceptor.
     @function
     @returns {number} The ID of the axios request interceptor
     */
    const interceptorRequest = setRequestInterceptor();

    /**
     Cleaning function to remove interceptors when component is unmounted.
     @function
     */
    return () => {
      api.interceptors.response.eject(interceptorResponse);
      api.interceptors.request.eject(interceptorRequest);
    };
  }, []);
};
